import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import { api } from "@/lib/api";

const HERO_BG = "https://images.unsplash.com/photo-1611591437281-460bfbe1220a?auto=format&fit=crop&w=2400&q=85";
const ATELIER_IMG = "https://images.unsplash.com/photo-1609619742069-f5e18afeef17?auto=format&fit=crop&w=1800&q=85";

const PRESS = ["Vogue India", "The Voice of Fashion", "Verve", "Hindustan Times", "Telegraph", "Economic Times", "Local Samosa", "iDiva"];

export default function Home() {
    const [featured, setFeatured] = useState([]);
    const [newArrivals, setNewArrivals] = useState([]);
    const [collections, setCollections] = useState([]);

    useEffect(() => {
        api.get("/products").then(({ data }) => {
            setNewArrivals(data.slice(0, 8));
            setFeatured(data.filter((p) => p.featured).slice(0, 6));
        });
        api.get("/collections").then(({ data }) => setCollections(data));
    }, []);

    return (
        <div className="bg-paper">
            {/* HERO */}
            <section className="relative">
                <div className="relative h-[62vh] min-h-[380px] sm:min-h-[460px] max-h-[680px] overflow-hidden">
                    <img src={HERO_BG} alt="Gold by SRN bridal collection" className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-paper/85 via-paper/35 to-transparent md:from-paper/65 md:via-paper/10" />
                    <div className="relative h-full max-w-[1480px] mx-auto px-6 lg:px-12 flex flex-col justify-center">
                        <div className="max-w-2xl fade-up">
                            <p className="overline">Manufacturer · Jaipur · Est. SRN</p>
                            <h1 className="font-serif text-4xl sm:text-5xl lg:text-[72px] leading-[0.98] text-ink mt-5">
                                Heritage gold,<br/>
                                <span className="serif-em text-bronze-deep">for the next generation</span> of jewellers.
                            </h1>
                            <p className="mt-6 text-charcoal text-base leading-relaxed max-w-lg">
                                We craft bridal &amp; everyday 22K collections for small jewellers across India. No retail. Just patient, honest manufacturing — and one direct WhatsApp line to our atelier.
                            </p>
                            <div className="mt-7 flex flex-wrap gap-4">
                                <Link to="/catalog" data-testid="hero-cta-catalog" className="group inline-flex items-center gap-3 px-8 py-3.5 bg-ink text-paper uppercase text-[11px] tracking-[0.26em] hover:bg-bronze-deep transition-colors">
                                    Shop the Catalog <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                                </Link>
                                <Link to="/contact" data-testid="hero-cta-contact" className="inline-flex items-center gap-3 px-8 py-3.5 border border-ink text-ink uppercase text-[11px] tracking-[0.26em] hover:bg-ink hover:text-paper transition-colors">
                                    Talk to Us
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* MARQUEE TRUST STRIP */}
            <section className="border-y border-soft bg-ivory overflow-hidden">
                <div className="flex whitespace-nowrap py-3 marquee">
                    {[...Array(2)].map((_, k) => (
                        <div key={`marquee-trust-${k}`} className="flex items-center gap-12 px-6 shrink-0">
                            {["BIS Hallmarked · 22K & 18K", "Hand-finished in Jaipur", "Low MOQ on every order", "Custom CAD designs", "Pan-India insured dispatch", "WhatsApp-direct quotations"].map((s) => (
                                <span key={s} className="text-[10px] uppercase tracking-[0.32em] text-charcoal">◆ {s}</span>
                            ))}
                        </div>
                    ))}
                </div>
            </section>

            {/* NEW ARRIVALS — full grid */}
            <section className="max-w-[1480px] mx-auto px-6 lg:px-12 pt-16 pb-20">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <p className="overline">Fresh on the Bench</p>
                        <h2 className="font-serif text-4xl sm:text-5xl text-ink mt-3">New Arrivals</h2>
                    </div>
                    <Link to="/catalog" className="hidden sm:inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-bronze-deep underline-grow" data-testid="home-link-all-catalog">
                        View All <ArrowRight size={12} />
                    </Link>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                    {newArrivals.map((p) => <ProductCard key={p.id} product={p} />)}
                </div>
                <div className="mt-12 text-center sm:hidden">
                    <Link to="/catalog" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-bronze-deep underline-grow">
                        View All Pieces <ArrowRight size={12} />
                    </Link>
                </div>
            </section>

            {/* COLLECTIONS — all real Shopify collections with cover images */}
            <section className="bg-ivory">
                <div className="max-w-[1480px] mx-auto px-6 lg:px-12 py-20">
                    <div className="text-center max-w-2xl mx-auto">
                        <p className="overline">Shop the Collections</p>
                        <h2 className="font-serif text-4xl sm:text-5xl text-ink mt-3">From bridal sets to everyday classics</h2>
                        <p className="mt-4 text-sm text-charcoal">All {collections.length} collections, straight from our atelier shelf.</p>
                    </div>
                    <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5 lg:gap-6">
                        {collections.map((c) => (
                            <Link
                                key={c.handle}
                                to={`/catalog?collection=${encodeURIComponent(c.handle)}`}
                                data-testid={`home-collection-${c.handle}`}
                                className="group block"
                            >
                                <div className="aspect-square overflow-hidden bg-paper border border-soft group-hover:border-bronze transition-colors">
                                    <img
                                        src={c.image || "https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=900"}
                                        alt={c.title}
                                        loading="lazy"
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                </div>
                                <div className="mt-3 flex items-baseline justify-between gap-2">
                                    <p className="font-serif text-xl text-ink leading-tight group-hover:text-bronze-deep transition-colors">{c.title}</p>
                                    <span className="text-[10px] uppercase tracking-[0.18em] text-muted-ink shrink-0">{c.products_count} pcs</span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* EDITORIAL BANNER */}
            <section className="bg-ivory">
                <div className="max-w-[1480px] mx-auto px-6 lg:px-12 py-20 grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-6 order-2 md:order-1">
                        <p className="overline">The Atelier</p>
                        <h2 className="font-serif text-4xl sm:text-5xl text-ink mt-3 leading-[1.05]">
                            Three generations.<br/>
                            <span className="serif-em text-bronze-deep">One workshop.</span>
                        </h2>
                        <p className="mt-6 text-charcoal leading-relaxed max-w-md">
                            SRN began as a single bench in the lanes of Johari Bazar. Today, we supply jewellers across India — manufacturing everything from antique kundan sets to modern stackables. We don't sell direct to consumers. Our entire output is built for the trade.
                        </p>
                        <ul className="mt-8 space-y-3 text-sm text-charcoal">
                            <li className="flex gap-3"><span className="text-bronze-deep">—</span><span>Low minimum order quantities on every piece</span></li>
                            <li className="flex gap-3"><span className="text-bronze-deep">—</span><span>Custom CAD and bespoke designs on request</span></li>
                            <li className="flex gap-3"><span className="text-bronze-deep">—</span><span>Transparent making charges, BIS hallmarking</span></li>
                            <li className="flex gap-3"><span className="text-bronze-deep">—</span><span>Private-label &amp; white-label production</span></li>
                        </ul>
                        <Link to="/about" data-testid="home-about-link" className="inline-block mt-10 underline-grow text-[11px] uppercase tracking-[0.24em] text-bronze-deep">
                            Read our story →
                        </Link>
                    </div>
                    <div className="md:col-span-6 order-1 md:order-2">
                        <img src={ATELIER_IMG} alt="Artisan at work" className="w-full aspect-[4/5] object-cover" />
                    </div>
                </div>
            </section>

            {/* FEATURED PIECES */}
            {featured.length > 0 && (
                <section className="max-w-[1480px] mx-auto px-6 lg:px-12 py-24">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <p className="overline">Editor's Picks</p>
                        <h2 className="font-serif text-4xl sm:text-5xl text-ink mt-3">Bridal Highlights</h2>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                        {featured.map((p) => <ProductCard key={p.id} product={p} />)}
                    </div>
                </section>
            )}

            {/* SPLIT BANNER */}
            <section className="grid md:grid-cols-2 border-t border-soft">
                {collections.slice(0, 2).map((c, i) => (
                    <Link key={c.handle} to={`/catalog?collection=${encodeURIComponent(c.handle)}`} data-testid={`home-banner-${c.handle}`} className="relative aspect-[5/4] md:aspect-auto md:min-h-[480px] overflow-hidden group">
                        <img src={c.image} alt={c.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
                        <div className="absolute inset-0 bg-ink/25 group-hover:bg-ink/35 transition-colors" />
                        <div className="relative h-full flex flex-col justify-end p-8 lg:p-12">
                            <p className="overline text-paper">{i === 0 ? 'Heritage' : 'Statement'}</p>
                            <h3 className="font-serif text-3xl sm:text-4xl text-paper mt-3">{c.title}</h3>
                            <span className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-paper">Explore →</span>
                        </div>
                    </Link>
                ))}
            </section>

            {/* AS SEEN IN */}
            <section className="border-t border-soft py-16 overflow-hidden bg-ivory">
                <p className="overline text-center">As referenced by jewellers in</p>
                <div className="mt-8 flex whitespace-nowrap marquee">
                    {[...Array(2)].map((_, k) => (
                        <div key={`marquee-press-${k}`} className="flex items-center gap-14 px-7 shrink-0">
                            {PRESS.map((p) => (
                                <span key={`${k}-${p}`} className="font-serif text-2xl lg:text-3xl italic text-charcoal/70">{p}</span>
                            ))}
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="bg-paper">
                <div className="max-w-[1480px] mx-auto px-6 lg:px-12 py-20 text-center">
                    <p className="overline">Ready to Stock</p>
                    <h2 className="font-serif text-4xl sm:text-5xl text-ink mt-3 max-w-3xl mx-auto">Build your next collection with our atelier.</h2>
                    <p className="mt-5 text-charcoal max-w-xl mx-auto">No middlemen. No call-centres. Just one direct WhatsApp line and three generations of craftsmen at your service.</p>
                    <div className="mt-8 flex flex-wrap gap-4 justify-center">
                        <Link to="/catalog" data-testid="home-cta-final-catalog" className="px-8 py-4 bg-ink text-paper uppercase text-[11px] tracking-[0.26em] hover:bg-bronze-deep">Browse Catalog</Link>
                        <Link to="/contact" data-testid="home-cta-final-contact" className="px-8 py-4 border border-ink text-ink uppercase text-[11px] tracking-[0.26em] hover:bg-ink hover:text-paper">Talk to us</Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
